<script setup lang="ts">
import { ref, onMounted } from "vue";
import { api, errorText } from "../api";
import { LinkIcon, CopyIcon, CloseIcon } from "../icons";
const props = defineProps<{
  dataset: string;
  view?: string;
  dashboard?: string;
  modal?: boolean;
}>();
const emit = defineEmits(["close"]);
const container = ref<HTMLDialogElement | HTMLDetailsElement>();
const share = ref<any>(null),
  busy = ref(false),
  error = ref(""),
  copied = ref(false);
const path = props.dashboard
  ? `/api/datasets/${props.dataset}/dashboards/${props.dashboard}/share`
  : `/api/datasets/${props.dataset}/views/${props.view}/share`;
async function act(method = "GET") {
  busy.value = true;
  error.value = "";
  copied.value = false;
  try {
    const result = await api(path, { method });
    share.value = method === "DELETE" ? null : result;
  } catch (e) {
    error.value = errorText(e);
  } finally {
    busy.value = false;
  }
}
async function copy() {
  try {
    await navigator.clipboard.writeText(share.value.url);
    copied.value = true;
  } catch {
    error.value = "Markér og kopiér linket i feltet.";
  }
}
onMounted(() => {
  if (container.value instanceof HTMLDialogElement) container.value.showModal();
  void act();
});
</script>
<template>
  <component
    :is="modal ? 'dialog' : 'details'"
    ref="container"
    :class="modal ? 'record-dialog public-share-dialog' : 'public-share'"
    :aria-label="modal ? 'Offentligt delingslink' : undefined"
    @cancel.prevent="emit('close')"
  >
    <div v-if="modal" class="dialog-heading">
      <h2>Offentligt delingslink</h2>
      <button
        type="button"
        class="icon-button"
        aria-label="Luk deling"
        @click="emit('close')"
      >
        <CloseIcon />
      </button>
    </div>
    <summary v-else><LinkIcon :size="16" />Offentligt delingslink</summary>
    <div class="share-content">
      <p>
        Alle med linket kan se
        {{ dashboard ? "dette dashboard" : "dette kort" }} uden login. Kun de
        gemte kolonner og beregninger deles.
        {{
          dashboard
            ? "Data opdateres automatisk, mens siden er åben."
            : "Data opdateres, når linket åbnes."
        }}
      </p>
      <p v-if="dashboard" class="muted small">
        Linket forbliver det samme. Når du ændrer opsætningen eller udvalget af
        kort, vælg “Opdatér delt dashboard” for at vise ændringerne på linket.
      </p>
      <p v-else class="muted small">
        Ændrer du opsætningen eller udvalget af kort, skal du oprette et nyt
        link. Det tidligere link bliver da slået fra.
      </p>
      <template v-if="share">
        <label
          >Delingslink<input
            :value="share.url"
            readonly
            @focus="($event.target as HTMLInputElement).select()"
        /></label>
        <div class="toolbar">
          <button type="button" class="button secondary" @click="copy">
            <CopyIcon :size="16" />{{
              copied ? "Kopieret" : "Kopiér link"
            }}</button
          ><a
            :href="share.url"
            target="_blank"
            rel="noreferrer"
            class="text-button"
            >Åbn deling</a
          ><button
            type="button"
            class="text-button danger"
            :disabled="busy"
            @click="act('DELETE')"
          >
            Slå link fra
          </button>
        </div>
      </template>
      <button
        type="button"
        class="button secondary"
        :disabled="busy"
        @click="act('POST')"
      >
        {{
          busy
            ? "Arbejder…"
            : share
              ? dashboard
                ? "Opdatér delt dashboard"
                : "Erstat med nyt link"
              : "Opret offentligt link"
        }}
      </button>
      <p v-if="error" role="alert" class="error">{{ error }}</p>
    </div>
  </component>
</template>
<style scoped lang="scss" src="../styles/components/PublicShare.scss"></style>
